import type { ReactNode } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AgentPanelProvider } from '@/components/AgentPanel';
import { AuthProvider } from './AuthProvider';
import { DemoModeProvider } from './DemoModeProvider';
import { FeatureFlagProvider } from './FeatureFlagProvider';
import { JupyterProvider } from './JupyterProvider';
import { KernelPoolProvider } from './KernelPoolProvider';
import { NavigationProvider } from './NavigationProvider';
import { ServiceTokensProvider } from './ServiceTokensProvider';
import { SharedStateProvider } from './SharedStateProvider';
import { SimStatusProvider } from './SimStatusProvider';
import { ThemeProvider } from './ThemeProvider';
import { WorkspaceProvider } from './WorkspaceProvider';
import { WorkspaceStatusProvider } from './WorkspaceStatusProvider';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Root provider component that composes all application providers in the correct order.
 * Provider order matters due to dependencies:
 * - ThemeProvider: No dependencies (can be outermost)
 * - FeatureFlagProvider: No dependencies, localStorage-backed beta flag opt-ins
 * - DemoModeProvider: No dependencies, provides demo mode state for filtering
 * - SharedStateProvider: Cross-tab sync, no dependencies on other providers
 * - BrowserRouter: Provides routing context
 * - AuthProvider: Wraps Auth0, no dependencies on other app providers
 * - ServiceTokensProvider: Depends on Auth0 (useAuth0)
 * - WorkspaceProvider: Depends on Auth0 (useAuth0) and SharedStateProvider
 * - NavigationProvider: Depends on BrowserRouter (useNavigate, useLocation)
 * - KernelPoolProvider: Depends on WorkspaceProvider, ServiceTokensProvider
 * - JupyterProvider: Legacy notebook kernel, depends on WorkspaceProvider
 * - AgentPanelProvider: wraps the AgentPanel component tree in its own
 *   reducer/transport context for the Antioch Agent WebSocket stream.
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <ThemeProvider>
      <FeatureFlagProvider>
        <DemoModeProvider>
          <SharedStateProvider>
            <BrowserRouter>
              <AuthProvider>
                <ServiceTokensProvider>
                  <WorkspaceProvider>
                    <NavigationProvider>
                      <KernelPoolProvider>
                        <WorkspaceStatusProvider>
                          <SimStatusProvider>
                            <JupyterProvider>
                              <AgentPanelProvider>{children}</AgentPanelProvider>
                            </JupyterProvider>
                          </SimStatusProvider>
                        </WorkspaceStatusProvider>
                      </KernelPoolProvider>
                    </NavigationProvider>
                  </WorkspaceProvider>
                </ServiceTokensProvider>
              </AuthProvider>
            </BrowserRouter>
          </SharedStateProvider>
        </DemoModeProvider>
      </FeatureFlagProvider>
    </ThemeProvider>
  );
}
